import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { useAuthStore } from '@/stores/auth.store'

interface RefreshTokenResponse {
  accessToken: string
  refreshToken?: string
}

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean
}

const apiClient = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor for auth token
apiClient.interceptors.request.use(
  (config) => {
    const { accessToken } = useAuthStore.getState()
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor for token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as RetryableRequestConfig | undefined

    // Skip retry for non-401 errors, missing config, or already retried requests
    if (
      error.response?.status !== 401 ||
      !originalRequest ||
      originalRequest._retry
    ) {
      return Promise.reject(error)
    }

    originalRequest._retry = true
    const { refreshToken, setTokens, logout } = useAuthStore.getState()

    // No refresh token available - logout and redirect
    if (!refreshToken) {
      logout()
      if (typeof window !== 'undefined') {
        window.location.href = '/login'
      }
      return Promise.reject(error)
    }

    try {
      const response = await axios.post<RefreshTokenResponse>(
        '/api/auth/refresh',
        { refreshToken }
      )
      const { accessToken: newAccessToken, refreshToken: newRefreshToken } = response.data

      if (!newAccessToken) {
        throw new Error('Invalid refresh response')
      }

      setTokens(newAccessToken, newRefreshToken || refreshToken)
      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`

      return apiClient(originalRequest)
    } catch (refreshError) {
      logout()
      if (typeof window !== 'undefined') {
        window.location.href = '/login'
      }
      return Promise.reject(refreshError)
    }
  }
)

export { apiClient }
